import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { FiLock, FiUser, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import toast from 'react-hot-toast';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
    checkSetupStatus();
  }, [isAuthenticated, navigate]);

  const checkSetupStatus = async () => {
    try {
      await api.login({ username: 'test', password: 'test' });
    } catch (error) {
      if (error.response?.data?.message === 'Invalid credentials') {
        setNeedsSetup(false);
      } else {
        // No users exist, need setup
        setNeedsSetup(true);
      }
    }
  };

  const onSubmit = async (data) => {
    if (needsSetup) {
      try {
        await api.setupAdmin(data);
        toast.success('Admin account created successfully');
        setNeedsSetup(false);
        // Now login with the new credentials
        const result = await login(data.username, data.password);
        if (result.success) {
          navigate('/admin/dashboard');
        }
      } catch (error) {
        toast.error('Failed to create admin account');
      }
    } else {
      const result = await login(data.username, data.password);
      if (result.success) {
        navigate('/admin/dashboard');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-lodge-brown-50 to-pine-green-50 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-lodge-brown-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FiLock className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-display text-lodge-brown-800">
              {needsSetup ? 'Setup Admin Account' : 'Admin Login'}
            </h1>
            <p className="text-mountain-gray-600 mt-2">
              {needsSetup 
                ? 'Create your first admin account to get started'
                : 'Enter your credentials to access the admin panel'
              }
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-mountain-gray-700 mb-2">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiUser className="h-5 w-5 text-mountain-gray-400" />
                </div>
                <input
                  {...register('username', { 
                    required: 'Username is required',
                    minLength: { value: 3, message: 'Username must be at least 3 characters' }
                  })}
                  type="text"
                  className="block w-full pl-10 pr-3 py-3 border border-mountain-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                  placeholder="Enter your username"
                />
              </div>
              {errors.username && (
                <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
              )}
            </div>

            {needsSetup && (
              <div>
                <label className="block text-sm font-medium text-mountain-gray-700 mb-2">
                  Email
                </label>
                <input
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  type="email"
                  className="block w-full px-3 py-3 border border-mountain-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                  placeholder="admin@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-mountain-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-mountain-gray-400" />
                </div>
                <input
                  {...register('password', { 
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  className="block w-full pl-10 pr-10 py-3 border border-mountain-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <FiEyeOff className="h-5 w-5 text-mountain-gray-400" />
                  ) : (
                    <FiEye className="h-5 w-5 text-mountain-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary flex items-center justify-center"
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                needsSetup ? 'Create Admin Account' : 'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-lodge-brown-600 hover:text-lodge-brown-700">
              ← Back to Guidebook
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;